import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ProModal from '../components/ProModal';

const s = {
  page: { maxWidth:1100, margin:'0 auto', padding:'32px 24px', background:'var(--paper)' },
  back: { display:'inline-flex', alignItems:'center', gap:6, color:'var(--muted)', fontSize:13, cursor:'pointer', marginBottom:24, fontFamily:'var(--font-mono)' },
  header: { borderBottom:'2px solid var(--ink)', paddingBottom:24, marginBottom:32 },
  nctId: { fontFamily:'var(--font-mono)', fontSize:13, color:'var(--muted-2)', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:8 },
  title: { fontFamily:'var(--font-display)', fontSize:'clamp(22px, 4vw, 36px)', lineHeight:1.2, marginBottom:16, color:'var(--ink)' },
  metaRow: { display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' },
  tag: { fontFamily:'var(--font-mono)', fontSize:11, padding:'3px 10px', border:'1px solid var(--line)', borderRadius:0, color:'var(--muted)' },
  statusTag: { fontFamily:'var(--font-mono)', fontSize:11, padding:'3px 10px', borderRadius:0 },
  saveBtn: { marginLeft:'auto', padding:'8px 16px', border:'1.5px solid var(--ink)', borderRadius:0, fontSize:13, background:'transparent', color:'var(--ink)', cursor:'pointer', display:'flex', alignItems:'center', gap:6 },
  saveBtnActive: { background:'var(--ink)', color:'var(--paper)', border:'1.5px solid var(--ink)' },
  grid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, marginBottom:32 },
  section: { marginBottom:32 },
  sectionTitle: { fontFamily:'var(--font-mono)', fontSize:11, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', color:'var(--ink)', marginBottom:16, paddingBottom:8, borderBottom:'1px solid var(--line)' },
  card: { background:'var(--card)', border:'1px solid var(--line-card)', borderRadius:0, padding:20 },
  label: { fontSize:11, color:'var(--muted-2)', fontFamily:'var(--font-mono)', letterSpacing:'1px', textTransform:'uppercase', marginBottom:4 },
  value: { fontSize:14, color:'var(--ink)', lineHeight:1.5 },
  row: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 },
  tabs: { display:'flex', gap:0, marginBottom:24, borderBottom:'1px solid var(--line)' },
  tab: { padding:'10px 20px', fontSize:13, fontWeight:500, cursor:'pointer', color:'var(--muted)', borderBottom:'2px solid transparent', transition:'all 0.15s' },
  tabActive: { color:'var(--ink)', borderBottom:'2px solid var(--ink)' },
  list: { paddingLeft:0, listStyle:'none' },
  listItem: { padding:'10px 0', borderBottom:'1px solid var(--line-card)', fontSize:14, color:'var(--muted)', lineHeight:1.5 },
  criterion: { padding:'8px 12px', background:'var(--paper-2)', borderRadius:0, fontSize:13, lineHeight:1.6, marginBottom:4, fontFamily:'var(--font-mono)', fontSize:12 },
  locationCard: { background:'var(--card)', border:'1px solid var(--line-card)', borderRadius:0, padding:'14px 16px', marginBottom:8 },
  locName: { fontWeight:600, fontSize:13, marginBottom:4, color:'var(--ink)' },
  locMeta: { fontSize:12, color:'var(--muted)', fontFamily:'var(--font-mono)' },
  resultsBtn: { display:'inline-flex', alignItems:'center', gap:8, padding:'10px 20px', background:'var(--ink)', color:'var(--paper)', borderRadius:0, fontSize:13, fontWeight:600, cursor:'pointer', border:'none' }
};

const STATUS_COLORS = {
  RECRUITING: { bg:'#dcfce7', color:'#15803d' },
  NOT_YET_RECRUITING: { bg:'#dbeafe', color:'#1d4ed8' },
  COMPLETED: { bg:'#f3f4f6', color:'#374151' },
  ACTIVE_NOT_RECRUITING: { bg:'#fef9c3', color:'#854d0e' },
  TERMINATED: { bg:'#fee2e2', color:'#b91c1c' },
  default: { bg:'#f3f4f6', color:'#374151' }
};

export default function StudyDetail() {
  const { nctId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser, isPro } = useAuth();
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [saved, setSaved] = useState(false);
  const [showPro, setShowPro] = useState(false);

  useEffect(() => {
    axios.get(`/api/trials/studies/${nctId}`)
      .then(({ data }) => { setStudy(data); setLoading(false); })
      .catch(() => setLoading(false));
    setSaved(user?.savedStudies?.includes(nctId));
  }, [nctId]);

  const handleSave = async () => {
    try {
      const { data } = await axios.post(`/api/trials/saved-studies/${nctId}`);
      setSaved(data.saved);
      refreshUser();
    } catch (err) {
      if (err.response?.data?.requiresPro) setShowPro(true);
    }
  };

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}><div className="spin" style={{ width:32, height:32, border:'2px solid var(--line)', borderTopColor:'var(--ink)', borderRadius:'50%' }} /></div>;
  if (!study) return <div style={{ textAlign:'center', padding:80 }}><h2>Study not found</h2></div>;

  const p = study.protocolSection;
  const id = p?.identificationModule;
  const status = p?.statusModule;
  const design = p?.designModule;
  const desc = p?.descriptionModule;
  const eligibility = p?.eligibilityModule;
  const contacts = p?.contactsLocationsModule;
  const arms = p?.armsInterventionsModule;
  const outcomes = p?.outcomesModule;
  const sponsor = p?.sponsorCollaboratorsModule;
  const results = study.resultsSection;
  const c = STATUS_COLORS[status?.overallStatus] || STATUS_COLORS.default;

  const tabs = [
    { id:'overview', label:'Overview' },
    { id:'eligibility', label:'Eligibility' },
    { id:'interventions', label:'Interventions' },
    { id:'outcomes', label:'Outcomes' },
    { id:'locations', label:`Locations (${contacts?.locations?.length || 0})` },
    ...(results ? [{ id:'results', label:'Results' }] : [])
  ];

  return (
    <div style={s.page} className="fade-in">
      {showPro && <ProModal onClose={() => setShowPro(false)} />}
      <div style={s.back} onClick={() => navigate(-1)}>← Back to search</div>

      <div style={s.header}>
        <div style={s.nctId}>{id?.nctId}</div>
        <h1 style={s.title}>{id?.briefTitle}</h1>
        <div className="study-meta-row" style={s.metaRow}>
          <span style={{ ...s.statusTag, background:c.bg, color:c.color }}>{status?.overallStatus?.replace(/_/g,' ')}</span>
          {design?.phases?.map(ph => <span key={ph} style={s.tag}>{ph.replace('PHASE','Phase ')}</span>)}
          {design?.studyType && <span style={s.tag}>{design.studyType}</span>}
          {sponsor?.leadSponsor?.name && <span style={s.tag}>{sponsor.leadSponsor.name}</span>}
          <button className="study-save-btn" style={{ ...s.saveBtn, ...(saved ? s.saveBtnActive : {}) }} onClick={handleSave}>
            {saved ? '★ Saved' : '☆ Save Study'}
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="study-stats-grid" style={s.grid}>
        {[
          { label:'Start Date', value: status?.startDateStruct?.date || '—' },
          { label:'Completion', value: status?.completionDateStruct?.date || '—' },
          { label:'Enrollment', value: design?.enrollmentInfo?.count ? `${Number(design.enrollmentInfo.count).toLocaleString()} ${design.enrollmentInfo.type || ''}` : '—' },
          { label:'Study Type', value: design?.studyType || '—' },
          { label:'Sponsor', value: sponsor?.leadSponsor?.name || '—' },
          { label:'Agency Class', value: sponsor?.leadSponsor?.class || '—' }
        ].map(item => (
          <div key={item.label} style={s.card}>
            <div style={s.label}>{item.label}</div>
            <div style={s.value}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="study-tabs" style={s.tabs}>
        {tabs.map(tab => (
          <div key={tab.id} style={{ ...s.tab, ...(activeTab === tab.id ? s.tabActive : {}) }} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </div>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div>
          {desc?.briefSummary && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Brief Summary</div>
              <p style={{ fontSize:15, lineHeight:1.7, color:'var(--ink)' }}>{desc.briefSummary}</p>
            </div>
          )}
          {desc?.detailedDescription && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Detailed Description</div>
              <p style={{ fontSize:14, lineHeight:1.8, color:'var(--muted)', whiteSpace:'pre-wrap' }}>{desc.detailedDescription}</p>
            </div>
          )}
          {p?.conditionsModule?.conditions?.length > 0 && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Conditions</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {p.conditionsModule.conditions.map((c, i) => <span key={i} style={{ ...s.tag, padding:'6px 14px', fontSize:13 }}>{c}</span>)}
              </div>
            </div>
          )}
          {p?.conditionsModule?.keywords?.length > 0 && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Keywords</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {p.conditionsModule.keywords.map((k, i) => <span key={i} style={{ ...s.tag, color:'var(--muted-2)', fontSize:12 }}>{k}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'eligibility' && eligibility && (
        <div>
          <div className="study-eligibility-row" style={s.row}>
            <div style={s.card}><div style={s.label}>Minimum Age</div><div style={s.value}>{eligibility.minimumAge || 'N/A'}</div></div>
            <div style={s.card}><div style={s.label}>Maximum Age</div><div style={s.value}>{eligibility.maximumAge || 'N/A'}</div></div>
            <div style={s.card}><div style={s.label}>Sex</div><div style={s.value}>{eligibility.sex || 'All'}</div></div>
            <div style={s.card}><div style={s.label}>Healthy Volunteers</div><div style={s.value}>{eligibility.healthyVolunteers ? 'Yes' : 'No'}</div></div>
          </div>
          {eligibility.eligibilityCriteria && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Eligibility Criteria</div>
              <pre style={{ ...s.criterion, whiteSpace:'pre-wrap', fontFamily:'var(--font-body)', fontSize:14, lineHeight:1.7 }}>{eligibility.eligibilityCriteria}</pre>
            </div>
          )}
        </div>
      )}

      {activeTab === 'interventions' && (
        <div>
          {arms?.armGroups?.length > 0 && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Arm Groups</div>
              {arms.armGroups.map((arm, i) => (
                <div key={i} style={{ ...s.card, marginBottom:12 }}>
                  <div style={{ fontWeight:600, marginBottom:4, fontSize:14 }}>{arm.label}</div>
                  <div style={{ ...s.tag, display:'inline-block', marginBottom:8 }}>{arm.type}</div>
                  {arm.description && <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.5 }}>{arm.description}</p>}
                </div>
              ))}
            </div>
          )}
          {arms?.interventions?.length > 0 && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Interventions</div>
              {arms.interventions.map((intr, i) => (
                <div key={i} style={{ ...s.card, marginBottom:12 }}>
                  <div style={{ fontWeight:600, marginBottom:4 }}>{intr.name}</div>
                  <span style={s.tag}>{intr.type}</span>
                  {intr.description && <p style={{ fontSize:13, color:'var(--muted)', marginTop:8, lineHeight:1.5 }}>{intr.description}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'outcomes' && (
        <div>
          {outcomes?.primaryOutcomes?.length > 0 && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Primary Outcomes</div>
              {outcomes.primaryOutcomes.map((o, i) => (
                <div key={i} style={{ ...s.card, marginBottom:10 }}>
                  <div style={{ fontWeight:600, marginBottom:4 }}>{o.measure}</div>
                  {o.description && <p style={{ fontSize:13, color:'var(--muted)' }}>{o.description}</p>}
                  {o.timeFrame && <div style={{ ...s.tag, display:'inline-block', marginTop:6 }}>Timeframe: {o.timeFrame}</div>}
                </div>
              ))}
            </div>
          )}
          {outcomes?.secondaryOutcomes?.length > 0 && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Secondary Outcomes ({outcomes.secondaryOutcomes.length})</div>
              {outcomes.secondaryOutcomes.slice(0,5).map((o, i) => (
                <div key={i} style={{ ...s.card, marginBottom:8 }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:2 }}>{o.measure}</div>
                  {o.timeFrame && <span style={{ ...s.tag, fontSize:11 }}>{o.timeFrame}</span>}
                </div>
              ))}
              {outcomes.secondaryOutcomes.length > 5 && <div style={{ color:'var(--muted-2)', fontSize:13, marginTop:8 }}>+{outcomes.secondaryOutcomes.length - 5} more</div>}
            </div>
          )}
        </div>
      )}

      {activeTab === 'locations' && (
        <div>
          {!contacts?.locations?.length ? <p style={{ color:'var(--muted-2)' }}>No location data available</p> : (
            contacts.locations.map((loc, i) => (
              <div key={i} style={s.locationCard}>
                <div style={s.locName}>{loc.facility}</div>
                <div style={s.locMeta}>{[loc.city, loc.state, loc.country].filter(Boolean).join(', ')}</div>
                {loc.status && <span style={{ ...s.tag, fontSize:11, marginTop:6, display:'inline-block' }}>{loc.status}</span>}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'results' && results && (
        <div>
          <div style={s.section}>
            <div style={s.sectionTitle}>Study Results Available</div>
            <a href={`https://clinicaltrials.gov/study/${nctId}`} target="_blank" rel="noopener noreferrer">
              <button style={s.resultsBtn}>View Full Results on ClinicalTrials.gov ↗</button>
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
